import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthenticatedApi } from './api';
import LoadingSpinner from './components/LoadingSpinner';
import { toast } from 'react-hot-toast';
import { getAuthorName } from './utils/authorName';
import { trackEvent } from './utils/analytics';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import './components/Comments.css';
import './Forum.css';

export default function Forum() {
  // Helper to check if author should use Study Hall logo
  const shouldUseLogo = (authorName) => {
    const name = authorName.toLowerCase().trim();
    return name === "'study hall' via study hall listserv" || name === "study hall admin";
  };

  // Thread type color coding
  const getThreadTypeColor = (type) => {
    const colorMap = {
      'source-request': '#e67e7e',      // Coral
      'steal-pitch': '#607d8b',         // Slate blue
      'discussion': '#38b44a',          // Green (matching News)
      'plug-your-work': '#e74c3c'       // Red
    };
    return colorMap[type] || '#e0e0e0';  // Default gray for unknown types
  };
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const { authenticatedFetch } = useAuthenticatedApi();
  
  const [threads, setThreads] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchAuthor, setSearchAuthor] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showNewThreadModal, setShowNewThreadModal] = useState(false);
  const [newThreadSubject, setNewThreadSubject] = useState('');
  const [newThreadContent, setNewThreadContent] = useState('');
  const [isSubmittingThread, setIsSubmittingThread] = useState(false);
  const hasInitiallyLoaded = useRef(false);
  const [profilePictures, setProfilePictures] = useState({});

  // Initialize Tiptap editor for new thread content
  const threadEditor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
      }),
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: 'Enter your message...',
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setNewThreadContent(editor.getText().trim());
    },
  });

  // Focus editor when modal opens
  useEffect(() => {
    if (showNewThreadModal && threadEditor) {
      const timer = setTimeout(() => {
        threadEditor.commands.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showNewThreadModal, threadEditor]);

  // Cleanup editor on unmount
  useEffect(() => {
    return () => {
      if (threadEditor) {
        threadEditor.destroy();
      }
    };
  }, [threadEditor]);

  // Load profile pictures for threads
  const loadProfilePictures = useCallback(async (threadsData) => {
    try {
      // Collect all unique member IDs from threads
      const memberIds = new Set();
      threadsData.forEach(thread => {
        if (thread.member_id) memberIds.add(thread.member_id);
        if (thread.last_post_member_id) memberIds.add(thread.last_post_member_id);
      });

      if (memberIds.size === 0) return;

      const memberIdsArray = Array.from(memberIds);
      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/profile-pictures/batch?memberIds=${memberIdsArray.join(',')}`);
      
      if (response.ok) {
        const data = await response.json();
        setProfilePictures(prev => {
          const newPictures = { ...prev };
          // The API returns { profilePictures: { member_id: { profile_picture, profile_picture_type } } }
          if (data.profilePictures) {
            Object.entries(data.profilePictures).forEach(([memberId, pic]) => {
              if (pic.profile_picture) {
                newPictures[memberId] = {
                  profile_picture: pic.profile_picture,
                  profile_picture_type: pic.profile_picture_type
                };
              }
            });
          }
          return newPictures;
        });
      } else {
        console.error('❌ Failed to load profile pictures:', response.status);
      }
    } catch (error) {
      console.error('❌ Error loading profile pictures:', error);
    }
  }, [authenticatedFetch]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Handle source request query parameter
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const params = new URLSearchParams(location.search);
      if (params.get('createSourceRequest') === 'true') {
        setNewThreadSubject('SOURCE REQUEST:');
        setShowNewThreadModal(true);
        // Clean up the URL
        navigate('/forum', { replace: true });
      }
    }
  }, [isAuthenticated, authLoading, location.search, navigate]);

  // Profile pictures are now loaded directly in the main forum threads query

  const fetchThreads = useCallback(async (currentOffset = 0, append = false, searchParams = null) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        // Only show full loading on initial load, not on searches
        if (currentOffset === 0 && !searchParams) {
          setLoading(true);
        }
        // searching state is set in handleSearch, not here
      }
      
      const params = new URLSearchParams();
      params.append('limit', '20');
      params.append('offset', currentOffset.toString());
      params.append('sortBy', 'last_post_date');
      params.append('sortOrder', 'DESC');
      
      // Use passed search parameters or current state
      const currentSearchQuery = searchParams?.searchQuery ?? searchQuery;
      const currentSearchAuthor = searchParams?.searchAuthor ?? searchAuthor;
      const currentHasSearched = searchParams?.hasSearched ?? hasSearched;
      
      // Add search parameters if provided and we've actually searched
      if (currentHasSearched && currentSearchQuery.trim()) {
        params.append('search', currentSearchQuery.trim());
      }
      if (currentHasSearched && currentSearchAuthor.trim()) {
        params.append('author', currentSearchAuthor.trim());
      }
      
      const url = `${process.env.REACT_APP_API_URL || '/api'}/forum/threads?${params.toString()}`;
      
      const response = await authenticatedFetch(url);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to fetch forum threads (${response.status})`);
      }
      
      const result = await response.json();
      
      if (append) {
        setThreads(prevThreads => [...prevThreads, ...result.data]);
      } else {
        setThreads(result.data);
      }
      
      setTotalCount(result.total);
      setHasMore(result.hasMore);
      setOffset(currentOffset + result.data.length);
      
      // Mark initial load as complete
      if (isInitialLoad) {
        setIsInitialLoad(false);
        setRetryCount(0); // Reset retry count on success
      }
    } catch (err) {
      console.error('❌ Fetch error:', err);
      
      // Retry logic for initial load failures (not for searches)
      if (isInitialLoad && retryCount < 3) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          fetchThreads(currentOffset, append, searchParams);
        }, 2000 * (retryCount + 1)); // Exponential backoff: 2s, 4s, 6s
        return; // Don't set loading to false yet
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setSearching(false);
    }
  }, [authenticatedFetch, hasSearched, isInitialLoad, retryCount, searchAuthor, searchQuery]);

  // Fetch threads on initial load only
  useEffect(() => {
    if (isAuthenticated && !hasInitiallyLoaded.current) {
      hasInitiallyLoaded.current = true;
      // Call fetchThreads directly without dependencies
      const loadInitialThreads = async () => {
        try {
          setLoading(true);
          
          const params = new URLSearchParams();
          params.append('limit', '20');
          params.append('offset', '0');
          params.append('sortBy', 'last_post_date');
          params.append('sortOrder', 'DESC');
          
          const url = `${process.env.REACT_APP_API_URL || '/api'}/forum/threads?${params.toString()}`;
          const response = await authenticatedFetch(url);
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `Failed to fetch forum threads (${response.status})`);
          }
          
          const result = await response.json();
          setThreads(result.data);
          setTotalCount(result.total);
          setHasMore(result.hasMore);
          setOffset(result.data.length);
          setIsInitialLoad(false);
          setRetryCount(0);
          
          // Load profile pictures for the threads (non-blocking)
          if (result.data && result.data.length > 0) {
            // Don't await this - let it load in the background
            loadProfilePictures(result.data).catch(err => {
              console.error('❌ Error loading profile pictures:', err);
            });
          }
        } catch (err) {
          console.error('❌ Fetch error:', err);
        } finally {
          setLoading(false);
        }
      };
      
      loadInitialThreads();
    }
  }, [isAuthenticated, authenticatedFetch, loadProfilePictures]);

  const loadMore = () => {
    fetchThreads(offset, true);
  };

  const handleSearch = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const searchLabel = [searchQuery && `keywords:${searchQuery}`, searchAuthor && `author:${searchAuthor}`].filter(Boolean).join('; ') || 'search';
    trackEvent('forum_search', 'forum', searchLabel);
    setOffset(0);
    setHasSearched(true);
    setSearching(true);
    // Trigger the search by calling fetchThreads with current search parameters
    fetchThreads(0, false, { searchQuery, searchAuthor, hasSearched: true });
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchAuthor('');
    setOffset(0);
    setHasSearched(false);
    setSearching(true);
    // Trigger a refresh to show all threads
    fetchThreads(0, false, { searchQuery: '', searchAuthor: '', hasSearched: false });
  };

  const hasActiveSearch = searchQuery || searchAuthor;

  // MenuBar component for the editor
  const ThreadMenuBar = () => {
    if (!threadEditor) {
      return null;
    }

    return (
      <div style={{
        border: '1px solid #ddd',
        borderBottom: 'none',
        borderRadius: '4px 4px 0 0',
        backgroundColor: '#f8f9fa',
        padding: '0.5rem',
        display: 'flex',
        gap: '0.25rem',
        flexWrap: 'wrap'
      }}>
        <button
          type="button"
          onClick={() => threadEditor.chain().focus().toggleBold().run()}
          disabled={!threadEditor.can().chain().focus().toggleBold().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: threadEditor.isActive('bold') ? '#38b44a' : 'white',
            color: threadEditor.isActive('bold') ? 'white' : '#333',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.875rem'
          }}
        >
          B
        </button>
        <button
          type="button"
          onClick={() => threadEditor.chain().focus().toggleItalic().run()}
          disabled={!threadEditor.can().chain().focus().toggleItalic().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: threadEditor.isActive('italic') ? '#38b44a' : 'white',
            color: threadEditor.isActive('italic') ? 'white' : '#333',
            cursor: 'pointer',
            fontStyle: 'italic',
            fontSize: '0.875rem'
          }}
        >
          I
        </button>
        <button
          type="button"
          onClick={() => threadEditor.chain().focus().toggleStrike().run()}
          disabled={!threadEditor.can().chain().focus().toggleStrike().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: threadEditor.isActive('strike') ? '#38b44a' : 'white',
            color: threadEditor.isActive('strike') ? 'white' : '#333',
            cursor: 'pointer',
            textDecoration: 'line-through',
            fontSize: '0.875rem'
          }}
        >
          S
        </button>
        <button
          type="button"
          onClick={() => threadEditor.chain().focus().toggleHeading({ level: 2 }).run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: threadEditor.isActive('heading', { level: 2 }) ? '#38b44a' : 'white',
            color: threadEditor.isActive('heading', { level: 2 }) ? 'white' : '#333',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => threadEditor.chain().focus().toggleBulletList().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: threadEditor.isActive('bulletList') ? '#38b44a' : 'white',
            color: threadEditor.isActive('bulletList') ? 'white' : '#333',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => threadEditor.chain().focus().toggleOrderedList().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: threadEditor.isActive('orderedList') ? '#38b44a' : 'white',
            color: threadEditor.isActive('orderedList') ? 'white' : '#333',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          1. List
        </button>
      </div>
    );
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();
    if (!threadEditor || !newThreadSubject.trim() || !newThreadContent.trim()) {
      toast.error('Please fill in both subject and content');
      return;
    }

    setIsSubmittingThread(true);
    try {
      // Get HTML content from editor
      const htmlContent = threadEditor.getHTML();
      
      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/forum/threads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: newThreadSubject.trim(),
          content: htmlContent,
          type: 'discussion'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create thread');
      }

      trackEvent('forum_thread_created', 'forum', newThreadSubject.trim());

      // Reset form and close modal
      setNewThreadSubject('');
      if (threadEditor) {
        threadEditor.commands.clearContent();
      }
      setNewThreadContent('');
      setShowNewThreadModal(false);
      
      // Refresh threads list to show the new thread at the top
      fetchThreads(0, false);
    } catch (err) {
      console.error('Error creating thread:', err);
      toast.error('Failed to create thread: ' + err.message);
    } finally {
      setIsSubmittingThread(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Unknown';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) {
        return 'just now';
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours} hour${hours === 1 ? '' : 's'} ago`;
      } else if (diffInSeconds < 604800) {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days} day${days === 1 ? '' : 's'} ago`;
      } else if (diffInSeconds < 2592000) {
        const weeks = Math.floor(diffInSeconds / 604800);
        return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
      } else if (diffInSeconds < 31536000) {
        const months = Math.floor(diffInSeconds / 2592000);
        return `${months} month${months === 1 ? '' : 's'} ago`;
      } else {
        const years = Math.floor(diffInSeconds / 31536000);
        return `${years} year${years === 1 ? '' : 's'} ago`;
      }
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getLastPostAuthor = (thread) => {
    // Priority 1: display_name (would need to be added to query)
    // Priority 2: first_name + last_name from members table
    if (thread.last_post_first_name && thread.last_post_last_name && 
        thread.last_post_first_name.trim() && thread.last_post_last_name.trim()) {
      return `${thread.last_post_first_name} ${thread.last_post_last_name}`;
    }
    // Priority 3: author_name from comments table
    if (thread.last_post_author && thread.last_post_author.trim()) {
      return thread.last_post_author;
    }
    return 'Unknown';
  };


  if (authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh'
      }}>
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh'
      }}>
        <LoadingSpinner message="Loading forum threads..." />
      </div>
    );
  }

  // Remove error display - just show loading spinner and let React Query retry

  return (
    <div className="forum-container" style={{ 
      maxWidth: '1000px', 
      margin: '0 auto', 
      padding: '1.5rem',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: '700', 
            color: '#333', 
            margin: '0'
          }}>
            Forum
          </h1>
          <button
            onClick={() => { trackEvent('forum_new_thread', 'forum', 'New Thread'); setShowNewThreadModal(true); }}
            style={{
              backgroundColor: '#38b44a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f3f'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#38b44a'}
          >
            ➕ New Thread
          </button>
        </div>
        {!loading && !searching && !isInitialLoad && (
          <p style={{ fontSize: '1rem', color: '#666' }}>
            {totalCount} discussion threads
          </p>
        )}
      </div>

      {/* Search Form */}
      <div style={{ 
        backgroundColor: 'white', 
        border: '1px solid #e5e7eb', 
        borderRadius: '0.5rem', 
        padding: '1.5rem', 
        marginBottom: '2rem',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          <div className="search-fields-flex" style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
            <div className="search-field-container" style={{ flex: '1 1 200px', minWidth: '200px', marginBottom: '0.5rem' }}>
              <label htmlFor="searchQuery" style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                🔍 Keywords
              </label>
              <input 
                type="text" 
                id="searchQuery" 
                style={{
                  width: '100%',
                  padding: '0.375rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  outline: 'none',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
                placeholder="Search thread subject and content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="search-field-container" style={{ flex: '1 1 200px', minWidth: '200px', marginBottom: '0.5rem' }}>
              <label htmlFor="searchAuthor" style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                👤 Author
              </label>
              <input 
                type="text" 
                id="searchAuthor" 
                style={{
                  width: '100%',
                  padding: '0.375rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  outline: 'none',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
                placeholder="Search by author name..."
                value={searchAuthor}
                onChange={(e) => setSearchAuthor(e.target.value)}
              />
            </div>
            <div className="search-field-container search-button-container" style={{ flex: '1 1 200px', minWidth: '200px', display: 'flex', alignItems: 'end', marginBottom: '0.5rem' }}>
              <div 
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (hasActiveSearch) {
                    handleSearch(e);
                  }
                  return false;
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (hasActiveSearch) {
                    handleSearch(e);
                  }
                  return false;
                }}
                style={{
                  width: '100%',
                  padding: '0.375rem 0.75rem',
                  backgroundColor: hasActiveSearch ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: hasActiveSearch ? 'pointer' : 'not-allowed',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  transition: 'background-color 0.2s',
                  textAlign: 'center',
                  userSelect: 'none',
                  opacity: hasActiveSearch ? 1 : 0.6
                }}
                onMouseOver={(e) => {
                  if (hasActiveSearch) {
                    e.target.style.backgroundColor = '#2563eb';
                  }
                }}
                onMouseOut={(e) => {
                  if (hasActiveSearch) {
                    e.target.style.backgroundColor = '#3b82f6';
                  }
                }}
              >
                🔍 Search
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {searchQuery && (
                <span style={{ 
                  fontSize: '0.75rem', 
                  backgroundColor: '#fef3c7', 
                  color: '#92400e', 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  Keywords: {searchQuery}
                  <button 
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setSearching(true);
                      // If this was the only search term, clear the search and show all threads
                      if (!searchAuthor.trim()) {
                        setHasSearched(false);
                        setOffset(0);
                        fetchThreads(0, false, { searchQuery: '', searchAuthor, hasSearched: false });
                      } else {
                        // Still have author search, so trigger search with remaining terms
                        setOffset(0);
                        fetchThreads(0, false, { searchQuery: '', searchAuthor, hasSearched: true });
                      }
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem' }}
                  >
                    ×
                  </button>
                </span>
              )}
              {searchAuthor && (
                <span style={{ 
                  fontSize: '0.75rem', 
                  backgroundColor: '#dbeafe', 
                  color: '#1e40af', 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  Author: {searchAuthor}
                  <button 
                    type="button"
                    onClick={() => {
                      setSearchAuthor('');
                      setSearching(true);
                      // If this was the only search term, clear the search and show all threads
                      if (!searchQuery.trim()) {
                        setHasSearched(false);
                        setOffset(0);
                        fetchThreads(0, false, { searchQuery, searchAuthor: '', hasSearched: false });
                      } else {
                        // Still have keyword search, so trigger search with remaining terms
                        setOffset(0);
                        fetchThreads(0, false, { searchQuery, searchAuthor: '', hasSearched: true });
                      }
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem' }}
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
            {hasActiveSearch && (
              <button 
                type="button"
                onClick={handleClearSearch}
                style={{
                  fontSize: '0.875rem',
                  color: '#2563eb',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
                onMouseOut={(e) => e.target.style.color = '#2563eb'}
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Loading indicator for searches */}
      {searching && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '2rem',
          color: '#666',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          margin: '1rem 0',
          minHeight: '100px'
        }}>
          <LoadingSpinner message="Searching..." />
        </div>
      )}

      {/* Threads List */}
      {loading && threads.length === 0 ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <LoadingSpinner message="Loading forum threads..." />
        </div>
      ) : searching ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: '#666'
        }}>
          {/* Threads are hidden while searching */}
        </div>
      ) : threads.length === 0 && hasSearched ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: '#666'
        }}>
          No threads found
        </div>
      ) : threads.length === 0 && isInitialLoad ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <LoadingSpinner message="Loading forum threads..." />
        </div>
      ) : threads.length === 0 ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: '#666'
        }}>
          No threads found
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {threads.map((thread) => (
            <div
              key={thread.id}
              className="forum-thread-card"
              style={{
                backgroundColor: 'white',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                border: `1px solid ${getThreadTypeColor(thread.type || 'discussion')}`,
                overflow: 'hidden'
              }}
              onClick={() => navigate(`/forum/${thread.id}`)}
            >
              {/* Colored left side patch */}
              <div style={{
                width: '6px',
                backgroundColor: getThreadTypeColor(thread.type || 'discussion'),
                flexShrink: 0,
                alignSelf: 'stretch'
              }}></div>
              
              {/* Content area with padding */}
              <div className="forum-thread-content-wrapper" style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                flex: 1
              }}>
              {/* Left: Author Image (50x50 placeholder) */}
              <div className="forum-thread-main-pic" style={{
                width: '50px',
                height: '50px',
                borderRadius: '4px',
                backgroundColor: shouldUseLogo(getAuthorName(thread)) ? 'transparent' : '#38b44a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '600',
                fontSize: '1.2rem',
                flexShrink: 0,
                overflow: 'hidden'
              }}>
                {shouldUseLogo(getAuthorName(thread)) ? (
                  <img 
                    src="/logo.png"
                    alt="Study Hall"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                  />
        ) : profilePictures[thread.member_id] ? (
          <img 
            src={`data:${profilePictures[thread.member_id].profile_picture_type || 'image/jpeg'};base64,${profilePictures[thread.member_id].profile_picture}`}
            alt={getAuthorName(thread)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '4px'
            }}
          />
        ) : (
          getAuthorName(thread).charAt(0).toUpperCase()
        )}
              </div>

              {/* Center: Title and Author Name */}
              <div className="forum-thread-info" style={{ flex: 1, minWidth: 0 }}>
                {/* Thread Title - desktop */}
                <h3 className="forum-thread-title" style={{ 
                  fontSize: '1rem', 
                  fontWeight: '600', 
                  color: '#333',
                  margin: '0 0 0.25rem 0',
                  lineHeight: '1.3'
                }}>
                  {thread.subject}
                </h3>
                
                {/* Author Name and Posted Time - desktop only */}
                <div className="forum-thread-meta forum-thread-meta-desktop" style={{ 
                  fontSize: '0.8rem', 
                  color: '#666',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>
                    {thread.points || 0} {Math.abs(thread.points || 0) === 1 ? 'point' : 'points'} • {getAuthorName(thread)} • {formatDate(thread.created_at)}
                  </span>
                </div>
              </div>

              {/* Right: Post Count and Last Post Info */}
              <div className="forum-thread-right" style={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'flex-end',
                fontSize: '0.8rem',
                color: '#666',
                flexShrink: 0,
                gap: '0.25rem'
              }}>
                <span className="forum-thread-post-count" style={{ 
                  backgroundColor: '#e9ecef',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '3px',
                  color: '#666',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  {thread.post_count} {thread.post_count === 1 ? 'post' : 'posts'}
                </span>
                {thread.last_post_date && thread.post_count > 1 && (
                  <div className="forum-thread-last-post-desktop" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.7rem',
                    color: '#999'
                  }}>
                    <div style={{
                      textAlign: 'right',
                      lineHeight: '1.2'
                    }}>
                      <div style={{ fontWeight: '500' }}>
                        {getLastPostAuthor(thread)}
                      </div>
                      <div style={{ color: '#aaa' }}>
                        {formatRelativeTime(thread.last_post_date)}
                      </div>
                    </div>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: shouldUseLogo(getLastPostAuthor(thread)) ? 'transparent' : '#38b44a',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '0.6rem',
                      flexShrink: 0,
                      overflow: 'hidden'
                    }}>
                      {shouldUseLogo(getLastPostAuthor(thread)) ? (
                        <img 
                          src="/logo.png"
                          alt="Study Hall"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '50%'
                          }}
                        />
        ) : profilePictures[thread.last_post_member_id] ? (
          <img 
            src={`data:${profilePictures[thread.last_post_member_id].profile_picture_type || 'image/jpeg'};base64,${profilePictures[thread.last_post_member_id].profile_picture}`}
            alt={getLastPostAuthor(thread)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '50%'
            }}
          />
        ) : (
          getLastPostAuthor(thread).charAt(0).toUpperCase()
        )}
                    </div>
                  </div>
                )}
              </div>
              </div>
              
              {/* Mobile: Thread Title - shown below main pic and post count */}
              <h3 className="forum-thread-title forum-thread-title-mobile" style={{ 
                fontSize: '1rem', 
                fontWeight: '600', 
                color: '#333',
                margin: 0,
                lineHeight: '1.3',
                padding: '0 0.75rem',
                paddingLeft: 'calc(6px + 0.75rem)',
                paddingBottom: '0.5rem',
                display: 'none'
              }}>
                {thread.subject}
              </h3>
              
              {/* Mobile: Last Post Info - its own line above points/author/date */}
              {thread.last_post_date && thread.post_count > 1 && (
                <div className="forum-thread-last-post-mobile" style={{ 
                  display: 'none',
                  fontSize: '0.8rem', 
                  color: '#666',
                  fontWeight: '500',
                  padding: '0.5rem 0.75rem',
                  paddingLeft: 'calc(6px + 0.75rem)',
                  borderTop: '1px solid #e9ecef',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: shouldUseLogo(getLastPostAuthor(thread)) ? 'transparent' : '#38b44a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '0.6rem',
                    flexShrink: 0,
                    overflow: 'hidden'
                  }}>
                    {shouldUseLogo(getLastPostAuthor(thread)) ? (
                      <img 
                        src="/logo.png"
                        alt="Study Hall"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '50%'
                        }}
                      />
                    ) : profilePictures[thread.last_post_member_id] ? (
                      <img 
                        src={`data:${profilePictures[thread.last_post_member_id].profile_picture_type || 'image/jpeg'};base64,${profilePictures[thread.last_post_member_id].profile_picture}`}
                        alt={getLastPostAuthor(thread)}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '50%'
                        }}
                      />
                    ) : (
                      getLastPostAuthor(thread).charAt(0).toUpperCase()
                    )}
                  </div>
                  <span style={{ fontWeight: '500' }}>
                    {getLastPostAuthor(thread)}
                  </span>
                  <span style={{ color: '#999', marginLeft: 'auto' }}>
                    {formatRelativeTime(thread.last_post_date)}
                  </span>
                </div>
              )}
              
              {/* Meta info for mobile - full width at bottom */}
              <div className="forum-thread-meta forum-thread-meta-mobile" style={{ 
                fontSize: '0.8rem', 
                color: '#666',
                fontWeight: '500',
                display: 'none'
              }}>
                <span>
                  {thread.points || 0} {Math.abs(thread.points || 0) === 1 ? 'point' : 'points'} • {getAuthorName(thread)} • {formatDate(thread.created_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load More Button */}
      {!loading && threads.length > 0 && hasMore && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '3rem',
          marginBottom: '2rem'
        }}>
          <button
            onClick={loadMore}
            disabled={loadingMore}
            style={{
              backgroundColor: loadingMore ? '#ccc' : '#38b44a',
              color: 'white',
              border: 'none',
              padding: '14px 32px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loadingMore ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => {
              if (!loadingMore) {
                e.target.style.backgroundColor = '#2d8f3a';
              }
            }}
            onMouseOut={(e) => {
              if (!loadingMore) {
                e.target.style.backgroundColor = '#38b44a';
              }
            }}
          >
            {loadingMore && <LoadingSpinner size="small" message="" />}
            {loadingMore ? 'Loading...' : 'Load More Threads'}
          </button>
        </div>
      )}

      {/* New Thread Modal */}
      {showNewThreadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#333',
                margin: 0
              }}>
                Create New Thread
              </h2>
              <button
                onClick={() => {
                  setShowNewThreadModal(false);
                  if (threadEditor) {
                    threadEditor.commands.clearContent();
                  }
                  setNewThreadContent('');
                  setNewThreadSubject('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateThread}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Subject *
                </label>
                <input
                  type="text"
                  value={newThreadSubject}
                  onChange={(e) => setNewThreadSubject(e.target.value)}
                  placeholder="Enter thread subject..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Content *
                </label>
                {threadEditor && (
                  <>
                    <ThreadMenuBar />
                    <div style={{ 
                      border: '1px solid #d1d5db',
                      borderRadius: '0 0 6px 6px',
                      backgroundColor: 'white',
                      minHeight: '200px',
                      padding: '0.75rem',
                      boxSizing: 'border-box'
                    }}>
                      <EditorContent editor={threadEditor} />
                    </div>
                  </>
                )}
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.75rem'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewThreadModal(false);
                    if (threadEditor) {
                      threadEditor.commands.clearContent();
                    }
                    setNewThreadContent('');
                    setNewThreadSubject('');
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingThread || !newThreadSubject.trim() || !newThreadContent.trim()}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: isSubmittingThread || !newThreadSubject.trim() || !newThreadContent.trim() ? '#9ca3af' : '#38b44a',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: isSubmittingThread || !newThreadSubject.trim() || !newThreadContent.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {isSubmittingThread && (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                  )}
                  {isSubmittingThread ? 'Creating...' : 'Create Thread'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
    </div>
  );
}
